import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  HomeOutlined,
  UserOutlined,
  PoweroffOutlined,
  CreditCardOutlined,
  WalletOutlined,
  DollarOutlined,
  SafetyOutlined,
  BankOutlined,
  TeamOutlined,
  AppstoreOutlined,
  ShopOutlined,
  FileTextOutlined,
  TagsOutlined,
  PictureOutlined,
  FireOutlined,
  AuditOutlined,
  SettingOutlined,
  LayoutOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import { useToast } from "../../components/toast";
import { useAuth } from "../../store/AuthContext";
import ConfirmModal from "../../components/modal/ConfirmModal";
import api from "../../api";
import logo from "../../assets/images/logo.png";
import { useLocation } from "react-router-dom";

const Section = ({ title, children }) => (
  <div className="sb-section">
    <div className="sb-section-title">{title}</div>
    <div className="sb-section-body">{children}</div>
  </div>
);

const Item = ({ to, icon, label }) => {
  const location = useLocation();

  // const active =
  //   location.pathname === to ||
  //   (location.pathname.startsWith(to + "/") &&
  //     !location.pathname.slice(to.length + 1).includes("/") === false);

  const active =
    location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <NavLink to={to} className={() => `sb-item ${active ? "active" : ""}`}>
      <span className="sb-ic">{icon}</span>
      <span className="sb-label">{label}</span>
    </NavLink>
  );
};

export default function Sidebar() {
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const toast = useToast();

  const { removeAuth, user } = useAuth();

  const userCardRef = useRef(null);
  const collapseRef = useRef(null);

  const ROLE_LABEL = {
    OWNER: "Супер админ",
    ADMIN: "Админ",
    EDITOR: "Редактор",
  };

  // ✅ Outside click → close
  useEffect(() => {
    const onDown = (e) => {
      if (!openUserMenu) return;
      if (!userCardRef.current) return;

      const inside = userCardRef.current.contains(e.target);
      if (!inside) setOpenUserMenu(false);
    };

    // mousedown + touchstart аль аль нь
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [openUserMenu]);

  // ✅ Smooth open/close without "max-height" glitch
  useEffect(() => {
    const el = collapseRef.current;
    if (!el) return;

    // нээлт/хаалт бүрд height-г inline тохируулна
    if (openUserMenu) {
      // 1) эхлээд 0 -> scrollHeight
      el.style.height = "0px";
      // force reflow
      void el.offsetHeight;
      el.style.height = `${el.scrollHeight}px`;
    } else {
      // 2) одоогийн height -> 0
      el.style.height = `${el.scrollHeight}px`;
      void el.offsetHeight;
      el.style.height = "0px";
    }
  }, [openUserMenu]);

  // ✅ transition дууссаны дараа open үед height: auto болгоод
  // контент нэмэгдэж/багасахад гацахгүй
  const onCollapseTransitionEnd = () => {
    const el = collapseRef.current;
    if (!el) return;
    if (openUserMenu) {
      el.style.height = "auto";
    }
  };

  const logoutHandler = async () => {
    const res = await api.logout();
    // console.log("logout res --->", res);
    if (res?.status === 200) {
      removeAuth();
      toast("Системээс гарлаа", "info");
    } else {
      // console.error("Logout error:", res);
      toast("Системээс гарахад алдаа гарлаа", "error");
    }
  };

  const isAdminOrSuper = ["ADMIN", "OWNER"].includes(user?.role);

  return (
    <aside className="sb">
      {/* Top */}
      <div className="sb-top">
        <div className="sb-brand">
          <img src={logo} alt="logo" className="sb-logo" />
        </div>
      </div>

      {/* User card */}
      <div
        className={`sb-user ${openUserMenu ? "open" : ""}`}
        ref={userCardRef}
      >
        <div className="sb-user-head">
          <div className="d-flex align-items-center gap-2 flex-1 min-w-0">
            <Avatar
              style={{
                backgroundColor: "var(--primary-100)",
                color: "var(--primary-600)",
                flexShrink: 0,
                width: "40px",
                height: "40px",
                fontWeight: 700,
              }}
            >
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </Avatar>
            <div className="sb-user-meta">
              <div className="sb-user-name">{user?.name || "-"}</div>
              <div className="sb-user-role">
                {ROLE_LABEL[user?.role] || "-"}
              </div>
            </div>
          </div>

          <button
            className="sb-user-toggle"
            type="button"
            onClick={() => setOpenUserMenu((v) => !v)}
            aria-expanded={openUserMenu}
            aria-label="Toggle user menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 6h16M7 12h10M10 18h4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div
          ref={collapseRef}
          className="sb-user-collapse"
          onTransitionEnd={onCollapseTransitionEnd}
        >
          <NavLink
            to="/settings"
            className="sb-user-item"
            onClick={() => setOpenUserMenu(false)}
          >
            <UserOutlined className="ic" />
            <span>Тохиргоо</span>
          </NavLink>

          <button
            type="button"
            className="sb-user-item danger"
            onClick={() => {
              setOpenUserMenu(false);
              setShowModal(true);
              // removeAuth(); гэх мэт
            }}
          >
            <PoweroffOutlined className="ic" />
            <span>Гарах</span>
          </button>
        </div>
      </div>

      {/* Menu */}
      <div className="sb-scroll">
        <Section title="ЕРӨНХИЙ">
          <Item to="/dashboard" icon={<HomeOutlined />} label="Дашбоард" end />
        </Section>

        <Section title="САНХҮҮ">
          <Item to="/card" icon={<CreditCardOutlined />} label="Карт" end />
          <Item to="/savings" icon={<WalletOutlined />} label="Хадгаламж" />
          <Item to="/loan" icon={<DollarOutlined />} label="Зээл" />
          <Item to="/insurance" icon={<SafetyOutlined />} label="Даатгал" />
          <Item to="/apartment" icon={<ShopOutlined />} label="Орон сууц" />
        </Section>

        <Section title="СУРТАЛЧИЛГАА">
          <Item to="/promotions" icon={<FireOutlined />} label="Хугацаат" />
        </Section>

        <Section title="КОНТЕНТ">
          <Item to="/news" icon={<FileTextOutlined />} label="Мэдээ" />
          <Item
            to="/news-taxonomies"
            icon={<TagsOutlined />}
            label={"Мэдээний ангилал"}
          />
          <Item to="/banners" icon={<PictureOutlined />} label="Баннер" />
        </Section>

        <Section title="ТОХИРГОО">
          <Item to="/issuers" icon={<BankOutlined />} label="Байгууллага" />
          <Item to="/categories" icon={<AppstoreOutlined />} label="Ангилал" />
          {isAdminOrSuper && (
            <Item
              to="/keywords"
              icon={<SettingOutlined />}
              label="Түлхүүр үгс"
            />
          )}
          <Item
            to="/home-products"
            icon={<LayoutOutlined />}
            label="Нүүр бүтээгдэхүүн"
          />
        </Section>

        <Section title="БУСАД">
          <Item to="/staff" icon={<TeamOutlined />} label="Ажилчид" />
          {isAdminOrSuper && (
            <Item to="/audit" icon={<AuditOutlined />} label="Үйлдлийн лог" />
          )}
        </Section>
      </div>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        submitHandler={logoutHandler}
        title="Системээс гарах уу?"
        variant="warning"
      />
    </aside>
  );
}
